import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  useGetMeasurementDeviceModelByIdHasDataQuery,
  useGetMeasurementDeviceModelByIdIsReferencedQuery,
  useGetMeasurementDeviceModelGetAllQuery,
} from '@redux/apiSlices/measurementDeviceModelApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GenericGridPage from '../GenericGridPageProps';
import { StyledEditIconWrapper } from '../StyledGridPageComponents';
import MeasuringDeviceModal from './MeasuringDeviceModal';

export type RowDataMeasuringDevice = {
  id: number;
  model: string;
  manufacturer: string;
  comment: string;
  isUsed?: boolean;
  noiseLimitCurrent?: string;
  noiseLimitVoltage?: string;
  rawNoiseLimitCurrent?: number;
  rawNoiseLimitVoltage?: number;
};

const MeasuringDeviceType = () => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [selected, setSelected] = useState<RowDataMeasuringDevice | null>(null);

  const { data: rows } = useGetMeasurementDeviceModelGetAllQuery();
  const { data: hasData } = useGetMeasurementDeviceModelByIdHasDataQuery(selected?.id ?? skipToken, {
    skip: !selected,
  });
  const { data: isReferenced } = useGetMeasurementDeviceModelByIdIsReferencedQuery(selected?.id ?? skipToken, {
    skip: !selected,
  });
  const formatNoiseValue = (value: number | null, unit: string) => (value !== null ? `${value} ${unit}` : '-');

  const transformedRows: RowDataMeasuringDevice[] = (rows?.externalDtos ?? []).map((item) => ({
    id: item.id ?? 0,
    model: item.model,
    manufacturer: item.manufacturer,
    comment: item.comment,
    noiseLimitCurrent: formatNoiseValue(item.noiseThresholdAmpere, 'A'),
    noiseLimitVoltage: formatNoiseValue(item.noiseThresholdVolt, 'V'),
    rawNoiseLimitCurrent: item.noiseThresholdAmpere,
    rawNoiseLimitVoltage: item.noiseThresholdVolt,
  }));

  const columns: GridColDef[] = [
    {
      field: 'manufacturer',
      headerName: t('masterdata.measuringDeviceType.table.manufacturer'),
      width: 270,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'model',
      headerName: t('masterdata.measuringDeviceType.table.model'),
      width: 270,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'noiseLimitVoltage',
      headerName: t('masterdata.measuringDeviceType.table.noiseLimitVoltage'),
      width: 270,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'noiseLimitCurrent',
      headerName: t('masterdata.measuringDeviceType.table.noiseLimitCurrent'),
      width: 270,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'comment',
      headerName: t('management.substation.comment'),
      flex: 1,
      width: 270,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'edit',
      headerName: '',
      width: 50,
      headerClassName: 'customBackgroundHeader',
      sortable: false,
      filterable: false,
      resizable: true,
      renderCell: (params) => (
        <StyledEditIconWrapper>
          <IconButtonTsoPq
            sizeVariant="s"
            buttonType="inputIconPrimary"
            icon={faPen}
            onClick={(event) => {
              event.stopPropagation();
              setShowModal(true);
              setSelected(params.row);
            }}
          />
        </StyledEditIconWrapper>
      ),
    },
  ];

  const modalComponent = showModal ? (
    <ModalDialogTsoPq modalState={showModal}>
      <MeasuringDeviceModal
        newMeasuringDevice={addNew}
        handleDiscard={() => {
          setShowModal(false);
          setAddNew(false);
          setSelected(null);
        }}
        selectedDevice={selected || undefined}
        updateDisabled={hasData}
        deleteDisabled={isReferenced}
        defaultValues={
          selected
            ? {
                ...selected,
                noiseThresholdVolt: selected.rawNoiseLimitVoltage,
                noiseThresholdAmpere: selected.rawNoiseLimitCurrent,
              }
            : {}
        }
      />
    </ModalDialogTsoPq>
  ) : null;

  return (
    <GenericGridPage<RowDataMeasuringDevice>
      title={t('masterdata.measuringDeviceType.section')}
      rows={transformedRows ?? []}
      columns={columns}
      onAddClick={() => {
        setAddNew(true);
        setShowModal(true);
      }}
      modalComponent={modalComponent}
      searchLabel={t('searchfield.search')}
      sortModel={[
        { field: 'manufacturer', sort: 'asc' },
        { field: 'model', sort: 'asc' },
      ]}
    />
  );
};

export default MeasuringDeviceType;
